import * as ReactDOMClient from "react-dom/client"
import { Provider } from "react-redux"
import { callFinished } from "../actions/calls"
import {
    setApiKey,
    setDestroy,
    setDialIn,
    setHangup,
    setHideSoftphone,
    setPostbackURL,
    setShowSoftphone,
    setDialParams,
} from "../actions/softphone"
import { setOptions } from "../actions/options"
import { reset as callIntellegenceReset } from "../actions/addon_call_intelligence"
import { disconnect, send as sendWS } from "../actions/websocket"
import { store } from "../data/store"
import SoftphoneComponent from "./SoftphoneComponent"
import axios from "axios"
import { setBackend } from "../config"
import { isNumber } from "../tools"
import { EnvInit as CallIntelligenceEnvInit } from "../components/call_intelligence/CallIntelligence.jsx"

function renderSoftphone(emit, enableAutoTests, destroy, registeredEvents) {
    const divEl = document.createElement("div")
    document.body.appendChild(divEl)
    const root = ReactDOMClient.createRoot(divEl)

    const eventHandler = {
        onRegistered: () => {
            console.log("[batchdialer-softphone] registered")
            emit("register")
        },
        onServerDisconnect: () => {
            console.log("[batchdialer-softphone] disconnected")
            emit("disconnected")
        },
        onUnregistered: () => {
            console.log("[batchdialer-softphone] unregistered")
            emit("unregistered")
        },
        onCallHangup: () => {
            destroy()
            store.dispatch(callFinished())
            console.log("[batchdialer-softphone] call hangup")
            emit("hangup")
        },
        onCallAnswered: () => {
            console.log("[batchdialer-softphone] call answered")
            emit("outgoing")
        },
        onCallReceived: session => {
            console.log("[batchdialer-softphone] call received")
            emit("incoming")
        },
        onCallError: () => {
            emit("callerror")
        },
        onHide: () => {
            console.log("[batchdialer-softphone] hide event")
            emit("hide")
        },
        onError: error => {
            destroy()
            console.log("[batchdialer-softphone] error ", error)
            emit("error", error)
        },
    }

    if ("click_help_wanted" in registeredEvents) {
        eventHandler["onClickHelpWanted"] = () => {
            console.log("[batchdialer-softphone] click_help_wanted")
            emit("click_help_wanted")
        }
    }

    if ("click_contact_open" in registeredEvents) {
        eventHandler["onClickContactOpen"] = contactid => {
            console.log("[batchdialer-softphone] click_contact_open")
            emit("click_contact_open", contactid)
        }
    }

    root.render(
        <Provider store={store}>
            <SoftphoneComponent eventHandler={eventHandler} enableAutoTests={enableAutoTests} />
        </Provider>
    )

    return [divEl, root]
}

export default class Softphone {
    events = {
        show: [],
        register: [],
        disconnected: [],
        unregistered: [],
        destroyed: [],
        hangup: [],
        outgoing: [],
        incoming: [],
        hide: [],
        error: [],
    }
    heartbeatTimeout = 25 * 1000
    timeout = null
    container = null
    root = null
    enableAutoTests = true

    constructor(props) {
        this.emit = this.emit.bind(this)
    }

    on(event, callback) {
        if (!this.events[event]) {
            this.events[event] = []
        }
        this.events[event].push(callback)
    }

    register(apikey, options = {}) {
        const rts = new Date().getTime()
        if (!this.registerTS) {
            this.registerTS = rts
        }

        return new Promise((resolve, reject) => {
            if (this.registerTS != rts) {
                reject("Already registred")
                return
            }

            const { backendAPI, backendWS, agent } = options || {}
            axios.defaults.headers.common["X-ApiKey"] = apikey
            setBackend(backendAPI, backendWS)

            // call additional
            const additional = {}
            if (agent?.id && `${agent.id}`.length <= 36) {
                additional.extagent = agent
            }

            var extMX // this mutex limit execution for only one event, not matter who it will (it here, because for inbound call we call incoming and outgoing event)
            const add2Call = () => {
                const intMX = new Date().getTime()
                if (!extMX) extMX = intMX
                if (extMX != intMX) return

                const t = setInterval(() => {
                    const { call, dialParams } = store.getState()
                    if (call && call?.id) {
                        if (dialParams?.contact?.vendorcontactid) {
                            additional.data = { vendor_contact_id: dialParams?.contact?.vendorcontactid }
                        }

                        clearInterval(t)

                        if (Object.keys(additional).length > 0) {
                            additional.callid = call.id
                            store.dispatch(sendWS("set_call_additional", additional))
                        }
                    }
                }, 300)
            }
            this.events.incoming.unshift(add2Call)
            this.events.outgoing.unshift(add2Call)

            // init Call Intelligence
            store.dispatch(callIntellegenceReset())
            CallIntelligenceEnvInit(store.dispatch)

            store.dispatch(setOptions(options))
            store.dispatch(setDestroy(false))
            store.dispatch(setApiKey(apikey))
            const [container, root] = renderSoftphone(this.emit, this.enableAutoTests, this.destroy, this.events)
            this.container = container
            this.root = root
            resolve()
        })
    }

    dial(params, param2) {
        return new Promise((resolve, reject) => {
            const pt = typeof params
            if (pt === "string") {
                if (!isNumber(params, true)) {
                    reject("Contact is not a phone number")
                    return
                }
                params = {
                    contact: { phonenumber: params },
                    callerID: typeof param2 === "string" && isNumber(param2, true) ? param2 : null,
                }
            } else if (pt === "object") {
                // depricated
                if (params?.phonenumber) {
                    if (!isNumber(params?.phonenumber, true)) {
                        reject("Contact object do not contains a phonenumber field or have a wrong format")
                        return
                    }
                    params = {
                        contact: params,
                        callerID: typeof param2 === "string" && isNumber(param2, true) ? param2 : null,
                    }
                }
            } else {
                reject("Unknown type of contact")
                return
            }

            const { contact, callerID } = params
            if (!contact || !isNumber(contact?.phonenumber, true)) {
                reject("Contact object do not contains a phonenumber field or have a wrong format")
                return
            }

            store.dispatch(setDialParams(params))
            store.dispatch(setShowSoftphone())
            if (typeof contact === "string") {
                store.dispatch(setDialIn({ number: contact, callerid: callerID }))
            } else if (typeof contact === "object") {
                store.dispatch(setDialIn({ number: contact.phonenumber, contact, callerid: callerID }))
            }

            resolve()
        })
    }

    show() {
        store.dispatch(setShowSoftphone())
    }

    hide() {
        store.dispatch(setHideSoftphone())
    }

    hangup() {
        store.dispatch(setHangup(true))
    }

    destroy = () => {
        store.dispatch(disconnect())
        store.dispatch(setDestroy(true))
        setTimeout(() => {
            if (this.container) {
                this.root.unmount()
                this.container.remove()
            }
            this.emit("destroyed")
            store.dispatch(setDestroy(false))
            this.registerTS = null
        }, 1000)
    }

    unregister(event) {
        if (!event && event in this.events[event]) {
            throw Error(event + " is unknown event")
        }

        this.events[event] = []
    }

    autoTests(value) {
        this.enableAutoTests = value
    }

    setPostbackURL(url) {
        store.dispatch(setPostbackURL(url))
    }

    emit(event, ...args) {
        if (!event && event in this.events[event]) {
            throw Error(event + " is unknown event")
        }

        if (!this.events[event].length) {
            // console.log(`No callback registerd for ${event}`)
        }
        this.events[event].forEach(cb => cb(...args))
    }
}
